import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import { flatMap } from 'rxjs/operators';
import { getCurrentDateToString, getServerDate, isStartAfterEnd, setServerDateToString } from '../../shared/helpers/time-helpers.model';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerLookupService } from '../customer-lookups.service';
import { CustomerCharacteristicAttribute } from '../models/customer-characteristic-attribute.model';
import { CustomerCharacteristic } from '../models/customer-characteristic.model';
import { CustomerCharacteristicType } from '../models/enums/customer-characteristic-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';
import { CustomerModalBaseComponent } from '../shared/customer-modal-base.component';

export class CharacteristicContext {
    constructor(public characteristicId: number, public customerNumberId: number, public isSexOffender: boolean = false, public deceased: boolean = false, public canSeeRedactedRecord: boolean = false, public canRemove: boolean = false) { }
}

@Component({
    templateUrl: './customer-characteristic-modal.html'
})
export class CustomerCharacteristicModalComponent extends CustomerModalBaseComponent {
    customerNumberId: number;
    characteristicId: number;
    isSexOffender: boolean;
    deceased: boolean;
    canRemove: boolean;

    // Represents any data that the characteristic had prior to the user making edits. Not altered after initialized.
    public characteristic: CustomerCharacteristic;
    private characteristics: CustomerCharacteristicType = new CustomerCharacteristicType();
    public canSeeRedactedRecord = false;
    public maxDate: Date;
    public form: UntypedFormGroup;
    public characteristicTypes: any[];
    public showButtonLoading = false;
    public showRemove = true;

    constructor(
        public location: PlatformLocation,
        public fb: UntypedFormBuilder,
        public service: CustomerManageService,
        public dialogRef: MatDialogRef<CustomerCharacteristicModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: CharacteristicContext,
        public modalService: MvdModalService,
        private lookupService: CustomerLookupService
    ) {
        super(modalService);

        this.customerNumberId = this.data.customerNumberId;
        this.characteristicId = this.data.characteristicId;
        this.isSexOffender = this.data.isSexOffender;
        this.deceased = this.data.deceased;
        this.canSeeRedactedRecord = this.data.canSeeRedactedRecord;
        this.canRemove = this.data.canRemove;

        this.characteristic = new CustomerCharacteristic();
        this.characteristic.attributes = [];
        this.characteristic.attributes.push(new CustomerCharacteristicAttribute());

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    public ngOnInit(): void {
        this.maxDate = new Date();

        this.form = this.fb.group({
            'characteristicTypeId': [null, Validators.required],
            'effectiveDate': [getCurrentDateToString(), Validators.required],
            'endDate': [null, this.endDateInRangeValidator]
        }, {
            validator: this.datesInOrderValidator
        });

        if (this.characteristicId != 0) {
            // The user is editing an existing characteristic
            this.initCharacteristic();
        } else {
            // The user is creating a new characteristic
            this.getCustomerType();
        }
    }

    private getCustomerType() {
        let customerType: number;
        this.service.getCustomerType(this.customerNumberId)
            .subscribe(result => customerType = result,
                e => this.handleValidationResult(e),
                () => {
                    this.initCharacteristicType(customerType);
                });
    }

    private initCharacteristicType(customerType: number) {
        this.lookupService.getData(customerType === 1 ? 'individual' : 'organization').subscribe(lookups => {
            this.characteristicTypes = lookups.result.customerCharacteristicTypes;
            if (customerType === 1) {
                this.characteristicTypes = this.filterIndividualCharacteristics();
            }
        });
    }

    /**
     * Loads the data for a pre-existing characteristic.
     * Called when the user wants to edit an existing characteristic, rather than creating a new one.
     **/
    private initCharacteristic(): void {
        this.service.getCustomerCharacteristic(this.characteristicId)
            .subscribe(result => {
                this.characteristic = result;
                if (this.characteristic.customerCharacteristicTypeId == this.characteristics.AddressConfidentialityProgram.id && !this.canRemove) {
                    this.showRemove = false;
                    this.form.get('endDate').disable();
                }
            },
            e => this.handleValidationResult(e),
            () => {
                this.form.get('characteristicTypeId').disable();
                this.form.get('characteristicTypeId').setValue(this.characteristic.customerCharacteristicTypeId);
                this.characteristicTypes = this.characteristics.GetActive().map(c => ({ value: c.id, text: c.displayValue }));
                const effectiveDate = getServerDate(this.characteristic, 'effectiveDate');
                this.form.get('effectiveDate').setValue(effectiveDate);
                const endDate = getServerDate(this.characteristic, 'endDate');
                this.form.get('endDate').setValue(endDate);
            });
    }

    private filterIndividualCharacteristics(): any[] {
        const self = this;
        let rTypes = this.characteristicTypes;
        if (this.isSexOffender) {
            rTypes = rTypes.filter(type => type.value !== this.characteristics.Transient.id.toString());
        }
        if (this.deceased) {
            rTypes = rTypes.filter(type => type.value != this.characteristics.AddressConfidentialityProgram.id.toString() && self.characteristicId != null);
        }
        if (!this.canSeeRedactedRecord) {
            rTypes = rTypes.filter(type => type.value !== this.characteristics.RestrictedRecord.id.toString());
        }
        return rTypes;
    }

    private characteristicTypeFromText(text: string): number {
        let rType = this.characteristics.GetActive().filter(type => type.displayValue == text);
        if (rType.length == 1) {
            return rType[0].id;
        }
        return 0;
    }

    private characteristicTypeFromId(id: number): string {
        let rType = this.characteristics.GetActive().filter(type => type.id == id);
        if (rType.length == 1) {
            return rType[0].displayValue;
        }
        return '';
    }

    public cancel() {
        this.dialogRef.close();
    }

    /**
     * Persists the customer characteristic that the user is currently editing, if valid.
     **/
    public saveCharacteristic(): void {
        if (this.form.valid) {
            this.showButtonLoading = true;
            const model = this.getModel();

            this.service.saveCustomerCharacteristic(model)
                .subscribe(result => {
                },
                e => {
                    this.showButtonLoading = false;
                    this.handleValidationResult(e);
                },
                () => {
                    this.showButtonLoading = false;
                    const isACP = (model.endDate == null || model.endDate == undefined || model.endDate > getCurrentDateToString());
                    this.dialogRef.close({ 'acpChanged': model.customerCharacteristicTypeId == this.characteristicTypeFromText('Address Confidentiality Program'), 'isACP': isACP });
                });
        }
    }

    /**
     * Checks whether the start date is before the end date.
     * @param group The form which supports this modal
     * returns an error if the start date is after the end date, null otherwise
     **/
    private datesInOrderValidator(group: UntypedFormGroup): ValidationErrors | null {
        const effectiveDate = group.get('effectiveDate').value;
        const endDate = group.get('endDate').value;
        if (isStartAfterEnd(effectiveDate, endDate)) {
            return { startAfterEndDate: true };
        }

        return null;
    }

    /**
     * Checks whether the end date is too far in the future.
     * @param control The control which contains the end date
     * @returns an error if the end date is too far in the future, null otherwise
     **/
    private endDateInRangeValidator(control: AbstractControl): ValidationErrors | null {
        const endDate = control.value;
        if (!endDate) {
            return null;
        }

        const endDateServer = getServerDate(endDate);
        var hundredYearsFromNow = new Date();
        hundredYearsFromNow.setFullYear(hundredYearsFromNow.getFullYear() + 100);
        const hundredYearsFromNowServer = getServerDate(hundredYearsFromNow);

        if (Date.parse(endDateServer) > Date.parse(hundredYearsFromNowServer)) {
            return { endDateTooFarInFuture: true };
        }

        return null;
    }

    /**
     * Creates a model which represents the characteristic that the user is currently editing.
     * @returns a model which represents the characteristic that the user is currently editing.
     **/
    private getModel(): CustomerCharacteristic {
        const model = Object.assign(new CustomerCharacteristic(), this.characteristic);
        model.customerNumberID = this.customerNumberId;
        model.customerCharacteristicTypeId = this.form.get('characteristicTypeId').value;
        model.effectiveDate = setServerDateToString(this.form.get('effectiveDate').value);
        model.endDate = setServerDateToString(this.form.get('endDate').value);

        return model;
    }

    /**
     * Removes the characteristic that the user is currently editing. and then persists this change.
     **/
    public deleteCustomerCharacteristic: Function = (): void => {
        const itemType = this.characteristicTypeFromId(this.characteristic.customerCharacteristicTypeId);
        this.endDateRecordConfirmation(itemType)
            .pipe(
                flatMap(() => {
                    this.form.get('endDate').setValue(getCurrentDateToString());
                    const model = this.getModel();
                    return this.service.saveCustomerCharacteristic(model);
                })
            )
            .subscribe(
                () => {
                    this.showButtonLoading = false;
                    this.dialogRef.close(true);
                },
                e => {
                    this.showButtonLoading = false;
                    this.handleValidationResult(e);
                }
            );
    };
}
